/**
 * FedRAMP profile options (V103-6-1 / V103-6-2), stored in
 * `certification_profiles.program_options_json` (no migration).
 *
 * `offering` holds the facts FedRAMP's Certification Package Overview schema
 * requires that the platform cannot derive (FRC-CSO-PKG via CPO-CSO-OVR,
 * CDS-CSO-PUB, CDS-CSO-SVC, CPO-CSO-MTD). Field names are ours; the CPO
 * builder maps them onto the FedRAMP schema. Enumerations mirror
 * fedramp-certification-package-overview-schema-2026-06-24.json so a value
 * accepted here is a value the schema accepts.
 *
 * `public_listing` is the provider's opt-in for the public CDS listing
 * (CDS-CSO-PUB / CDS-CSO-SVC): which fields are public. Default: none, so
 * nothing is public until the provider chooses. `listing_id` is minted by
 * the server and is the only identifier the public surface ever shows.
 */
import { z } from 'zod';
export const FEDRAMP_SERVICE_MODELS = ['SaaS', 'PaaS', 'IaaS'];
export const FEDRAMP_DEPLOYMENT_MODELS = [
    'Public Cloud', 'Government-Only Cloud', 'Hybrid Cloud', 'Community Cloud', 'Government Community Cloud',
];
export const FEDRAMP_BUSINESS_CATEGORIES = [
    'Accounting', 'Analytics', 'Artificial Intelligence (AI)', 'Collaboration', 'Communication', 'Construction',
    'Contact Center', 'Content Management System (CMS)', 'Customer Relations Management (CRM)', 'Customer Service',
    'Cybersecurity & Risk Management', 'Data Management', 'Design & Multimedia', 'Development Tools',
    'Education & Training', 'Finance', 'Fleet Management', 'Governance, Risk, and Compliance (GRC)', 'Grant Management',
    'Health & Wellness', 'Human Resources', 'Law Enforcement', 'Learning Management', 'Legal & Policy',
    'Marketing & Sales', 'Media', 'Mobile Device Management (MDM)', 'Network Management', 'Operations Management',
    'Research', 'Storage', 'System Administration', 'Talent Development', 'Talent Management', 'Travel Management',
    'Virtual Private Network (VPN)',
];
/** Fields a provider may make public on the CDS listing (CDS-CSO-PUB's list, plus names). */
export const FEDRAMP_PUBLIC_LISTING_FIELDS = [
    'provider_name', 'service_name', 'service_acronym', 'service_description', 'certification_type',
    'fedramp_id', 'uei_number', 'website', 'logo', 'service_model', 'deployment_model', 'business_category',
    'sales_contact', 'security_contact', 'services', 'secure_configuration_guidance', 'documentation',
    'trust_center', 'next_ongoing_certification_report_date', 'assessor',
];
const httpsUrl = z.string().trim().max(2048).url().refine((u) => /^https?:\/\//i.test(u), 'Must be an http(s) URL');
const text = (max) => z.string().trim().min(1).max(max);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const RepositoryBase = z.object({
    url: httpsUrl,
    description: text(2000),
    authentication_required: z.boolean(),
    access_instructions: text(4000).optional(),
});
const needsInstructions = (r) => !r.authentication_required || !!r.access_instructions;
const instructionsError = { message: 'access_instructions are required when authentication_required is true', path: ['access_instructions'] };
const Repository = RepositoryBase.strict().refine(needsInstructions, instructionsError);
/** A documentation repository (CDS-CSO-PUB "overview of documentation"); `types` are FedRAMP repositoryType values. */
const DocumentationRepository = RepositoryBase.extend({ types: z.array(text(80)).min(1).max(10) }).strict().refine(needsInstructions, instructionsError);
const Contact = z.object({
    /** FedRAMP contactType; CDS-CSO-PUB needs at least one Security and one Sales contact. */
    type: text(60),
    name: text(200).optional(),
    email: z.string().trim().email().max(254).optional(),
    phone: z.string().regex(/^[0-9]{3}-[0-9]{3}-[0-9]{4}$/, 'Phone must be ###-###-####').optional(),
}).strict();
export const FedrampOffering = z.object({
    fedramp_id: text(100),
    uei_number: z.string().regex(/^[A-Z0-9]{12}$/, 'A UEI is 12 letters or digits'),
    provider_name: text(200),
    service_name: text(200),
    service_acronym: text(40),
    service_description: text(20000),
    website: httpsUrl,
    logo_url: httpsUrl.refine((u) => /\.(png|jpe?g|gif|svg|webp|ico|bmp|tiff?)([?#].*)?$/i.test(u), 'Logo must link to an image file'),
    service_models: z.array(z.enum(FEDRAMP_SERVICE_MODELS)).min(1).max(3),
    deployment_model: z.enum(FEDRAMP_DEPLOYMENT_MODELS),
    business_categories: z.array(z.enum(FEDRAMP_BUSINESS_CATEGORIES)).min(1).max(10),
    contacts: z.array(Contact).max(20),
    trust_center: Repository,
    secure_configuration_guidance: Repository,
    additional_repositories: z.array(DocumentationRepository).max(20),
    assessor: z.object({ name: text(200), fedramp_id: z.string().regex(/^\d{6}$/, 'Assessor FedRAMP ID is 6 digits') }).strict(),
    advisors: z.array(z.object({ name: text(200) }).strict()).max(20),
    /** CDS-CSO-SVC: services in the offering, as named in public marketing. */
    services: z.array(z.object({ name: text(200), description: text(4000), date_available: isoDate.optional() }).strict()).max(500),
    /** CPO-CSO-MTD: official responsible and accountable for the package. */
    responsible_official: z.object({ name: text(200), title: text(200), email: z.string().trim().email().max(254), phone: text(40).optional() }).strict(),
    /** Full URI where the CPO is published (SDR / OCR `certificationPackageOverviewUri`). */
    certification_package_overview_url: httpsUrl,
}).partial().strict();
export const FedrampPublicListing = z.object({
    /** Server-minted; ignored on input. */
    listing_id: z.string().regex(/^fdl_[a-z0-9]{20,40}$/).optional(),
    fields: z.array(z.enum(FEDRAMP_PUBLIC_LISTING_FIELDS)).max(FEDRAMP_PUBLIC_LISTING_FIELDS.length),
}).strict();
/**
 * Options as a client may send them when creating a profile. The public
 * listing is not settable here: it is managed through the offering endpoint,
 * which mints the listing id (a client-chosen id could shadow another
 * tenant's public listing).
 */
export const FedrampProfileOptionsInput = z.object({
    /** Read by the ChatGPT compliance adapter (FedRAMP log retention). */
    log_retention_days: z.number().int().min(1).max(3650).optional(),
    offering: FedrampOffering.optional(),
    /**
     * V103-6-8: the date the Class A offering was first listed on the FedRAMP
     * Marketplace (Initial Implementation Phase). Anchors AGU-USE-CLA (agency
     * use beyond 12 months) and MKT-IIP-DLA (B/C/D assessment scheduled within
     * 2 years). Class A only.
     */
    class_a_listing_date: isoDate.optional(),
    /**
     * V103-6-3: the date the provider PLANS to be listed (may be in the future). The Class A clocks and
     * Today run from it, as projections, until `class_a_listing_date` (a real event) is set. Class A only.
     */
    class_a_target_listing_date: isoDate.optional(),
    /** V103-6-8: the date a Class B, C or D assessment was scheduled (MKT-IIP-DLA evidence; AGU-USE-CLA "actively seeking"). */
    higher_class_assessment_scheduled_on: isoDate.optional(),
    /**
     * V103-10-3: the provider-declared FIPS 199 security category of the
     * offering. FedRAMP classes have "not a direct correlation" to NIST impact
     * levels (fedramp.gov, Certification Classes), so nothing derives it from
     * the class; unset means not declared.
     */
    security_category: z.enum(['low', 'moderate', 'high'], { error: 'security_category must be low, moderate or high.' }).optional(),
}).strict();
/** Options as stored (input fields + the server-managed public listing). */
export const FedrampProfileOptions = FedrampProfileOptionsInput.extend({
    public_listing: FedrampPublicListing.optional(),
}).strict();
/** Parsed options from a stored row; invalid or absent → {} (never throws). */
export function readFedrampOptions(json) {
    try {
        const parsed = FedrampProfileOptions.safeParse(JSON.parse(json || '{}'));
        return parsed.success ? parsed.data : {};
    }
    catch {
        return {};
    }
}
//# sourceMappingURL=fedramp-options.js.map